import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChipGroup, Field, Section } from "@/components/yarn/Field";
import { api, errorText, THEMES } from "@/lib/api";

const secs = (list) => list.map((n) => ({ value: n, label: `${n}s` }));

export function CreateGameDialog({ open, onOpenChange, user }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: `${user.username}'s yarn`,
    theme: "comedy",
    visibility: "public",
    age_rating: user.is_adult ? "adult" : "underage",
    round_seconds: 60,
    vote_seconds: 30,
    max_players: 8,
  });
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/rooms", form);
      navigate(`/room/${data.code}`);
    } catch (err) {
      toast.error(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl border-2 bg-[var(--yarn-cream)] sm:max-w-lg" data-testid="create-game-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-2xl">Start a new yarn</DialogTitle>
          <DialogDescription>Pick the vibe and the rules. You'll be the host.</DialogDescription>
        </DialogHeader>
        <form onSubmit={create} className="space-y-5">
          <Field label="Game name" id="game-name" value={form.name} maxLength={40} required
            onChange={(e) => set("name")(e.target.value)} data-testid="create-game-name-input" />
          <Section label="Story theme" hint="The AI writes the opening line in this style.">
            <ChipGroup options={THEMES.map((t) => ({ value: t.key, label: t.label }))} value={form.theme}
              onChange={set("theme")} testid="create-theme" />
          </Section>
          <Section label="Who can join" hint={form.visibility === "public" ? "Listed in the lobby for anyone to join." : "Hidden. Only people with your game code can join."}>
            <ChipGroup options={[{ value: "public", label: "Public" }, { value: "private", label: "Private (code only)" }]}
              value={form.visibility} onChange={set("visibility")} testid="create-visibility" />
          </Section>
          <Section label="Age group" hint={form.age_rating === "adult" ? "Only players 18 or older can see or join." : "Open to everyone. Swear words get masked and starters stay family-friendly."}>
            <ChipGroup options={[{ value: "adult", label: "18+", disabled: !user.is_adult }, { value: "underage", label: "Underage" }]}
              value={form.age_rating} onChange={set("age_rating")} testid="create-age" />
          </Section>
          <Section label="Seconds to write each line">
            <ChipGroup options={secs([30, 45, 60, 90, 120])} value={form.round_seconds} onChange={set("round_seconds")} testid="create-round-seconds" />
          </Section>
          <Section label="Seconds to vote">
            <ChipGroup options={secs([20, 30, 45, 60])} value={form.vote_seconds} onChange={set("vote_seconds")} testid="create-vote-seconds" />
          </Section>
          <Section label="Max players" hint="3 is the minimum to play. 10 is the cap so voting stays fun.">
            <ChipGroup options={[4, 6, 8, 10].map((n) => ({ value: n, label: `${n}` }))} value={form.max_players}
              onChange={set("max_players")} testid="create-max-players" />
          </Section>
          <button type="submit" className="btn-yarn w-full h-14 text-lg" disabled={busy} data-testid="create-game-submit-button">
            {busy ? "Spinning up..." : "Create game"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
